import { keepPreviousData, useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { customerService } from '../../services/api';
import { retryOnServerError } from '../dashboard/queryOptions';

// Contacts (GET /conversations/contacts/) are gated by
// conversation.contact.view: a 403 is an expected outcome, rendered by the
// caller and never retried. Without contact.view_sensitive the API masks
// phone and e-mail; the screens just show what comes.

/** One page of contacts, for a server-side DataGrid (`queryOptions` as in parseQueryOptions). */
export const useContactsQuery = (queryOptions, { enabled = true } = {}) =>
  useQuery({
    enabled,
    queryKey: ['contacts', 'page', queryOptions],
    queryFn: () => customerService.getContacts(queryOptions).then((res) => res.data),
    placeholderData: keepPreviousData,
    retry: retryOnServerError,
  });

export const CONTACTS_PAGE_SIZE = 24;

/**
 * Contacts loaded page by page as the list scrolls (card view). Same
 * `queryOptions` as `useContactsQuery`, minus the pagination. `enabled`
 * lets a page with both views fetch only the one on screen.
 */
export const useContactsInfiniteQuery = (queryOptions, { enabled = true } = {}) =>
  useInfiniteQuery({
    enabled,
    queryKey: ['contacts', 'infinite', queryOptions],
    queryFn: ({ pageParam }) =>
      customerService
        .getContacts({ ...queryOptions, paginationModel: { page: pageParam, pageSize: CONTACTS_PAGE_SIZE } })
        .then((res) => res.data),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => (lastPage.next ? allPages.length : undefined),
    retry: retryOnServerError,
  });

/** A single contact (GET /conversations/contacts/{uuid}/), with stats and insights. */
export const useContactQuery = (uuid) =>
  useQuery({
    queryKey: ['contact', uuid],
    queryFn: () => customerService.getContact(uuid).then((res) => res.data),
    enabled: !!uuid,
    staleTime: 30_000,
    retry: retryOnServerError,
  });
